import { useEffect, useState } from "react";
import { EditIcon } from "assets/icons";
import Button from "components/BaseWidgets/button";
import Divider from "components/BaseWidgets/divider";
import Tag from "components/BaseWidgets/tag";
import PickerModal from "components/Onboard/components/PickerModal";
import { useInterests, usePersonalities } from "hooks/api/useListing";
import { formatDataWithIcons } from "utils/formatHelper";
import { useEditProfile } from "hooks/api/useProfile";
import { useQueryClient } from "@tanstack/react-query";

function InterestsPanel({
  id,
  isEdit,
  interests = [],
  personality = [],
  isLock,
}) {
  const [openInterest, setOpenInterest] = useState(false);
  const [openPersonality, setOpenPersonality] = useState(false);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [selectedPersonality, setSelectedPersonality] = useState([]);
  const queryClient = useQueryClient();
  const { data: interestData, isLoading: isInterestPending } = useInterests();
  const { data: personalityData, isLoading: isPersonalityPending } =
    usePersonalities();
  const interestsData = formatDataWithIcons(interestData || []);
  const personalitiesData = formatDataWithIcons(personalityData || []);

  const editProfileMutation = useEditProfile();
  useEffect(() => {
    setSelectedInterests(interests.map((item) => item.id));
    setSelectedPersonality(personality.map((item) => item.id));
  }, [interests, personality]);

  const findTag = (tagKey, data) => {
    for (const category of Object.values(data)) {
      const matchedTag = category.find((item) => item.key === tagKey);
      if (matchedTag) return matchedTag;
    }
    return null;
  };
  const renderTags = (tags, data, type) => {
    return tags.map((tagKey) => {
      const matchedTag = findTag(tagKey, data);
      const tagLabel = matchedTag?.label || tagKey?.toString() || "Unknown";
      return (
        <Tag
          key={tagKey}
          icon={
            matchedTag?.icon ? (
              <img
                src={matchedTag.icon}
                alt={tagLabel}
                className="w-5 h-5 object-contain selection-icon"
              />
            ) : null
          }
          onClick={() => handleRemoveTag(tagKey, type)}
        >
          {tagLabel}
        </Tag>
      );
    });
  };

  const preparePayload = (interestsArr, personalityArr) => ({
    interests: interestsArr,
    personality: personalityArr,
  });

  const handleMutationSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["my-profile"] });
  };

  return (
    <>
      <div id={id} className="interests-panel-frame panel-frame">
        <div className="interests-details">
          <div className="header flex items-center justify-between">
            <label className="interests-label font-title-normal">
              Interests
            </label>
            {isEdit && (
              <Button
                onClick={() => setOpenInterest(true)}
                variant="custom"
                className="edit-button flex items-center gap-1"
              >
                <EditIcon /> Edit
              </Button>
            )}
          </div>
          <div
            className={`tag-wrapper interests-tags flex flex-wrap gap-2 mt-4 ${isLock && "locked-card"}`}
          >
            {interests.length
              ? renderTags(selectedInterests, interestsData, "interests")
              : "-"}
          </div>
        </div>

        <Divider className="divider" />
        <div className="personality-details">
          <div className="header flex items-center justify-between">
            <label className="personality-label font-title-normal">
              Personality
            </label>
            {isEdit && (
              <Button
                onClick={() => setOpenPersonality(true)}
                variant="custom"
                className="edit-button flex items-center gap-1"
              >
                <EditIcon /> Edit
              </Button>
            )}
          </div>
          <div
            className={`tag-wrapper personality-tags flex flex-wrap gap-2 mt-4 ${isLock && "locked-card"}`}
          >
            {personality.length
              ? renderTags(
                  selectedPersonality,
                  personalitiesData,
                  "Personality",
                )
              : "-"}
          </div>
        </div>
      </div>

      <PickerModal
        open={openInterest}
        onCancel={() => setOpenInterest(false)}
        onOk={(tags) => {
          setSelectedInterests(tags);
          const payload = {
            ...preparePayload(tags, selectedPersonality),
            is_interest: true,
            is_personality: true,
          };
          editProfileMutation.mutate(payload, {
            onSuccess: handleMutationSuccess,
          });

          setOpenInterest(false);
        }}
        title="Select your Interests"
        description="Choose the interests to help finding the perfect matches."
        selectedTags={selectedInterests}
        data={interestsData || []}
        popup="interests"
      />

      <PickerModal
        open={openPersonality}
        onCancel={() => setOpenPersonality(false)}
        onOk={(tags) => {
          setSelectedPersonality(tags);
          const payload = {
            ...preparePayload(selectedInterests, tags),
            is_interest: true,
            is_personality: true,
          };
          editProfileMutation.mutate(payload, {
            onSuccess: handleMutationSuccess,
          });

          setOpenPersonality(false);
        }}
        title="Select your Personality"
        description="Choose the personality to help finding the perfect matches."
        selectedTags={selectedPersonality}
        data={personalitiesData || []}
        popup="personality"
      />
    </>
  );
}

export default InterestsPanel;
